import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Typography, TextField,
  Grid, Button, Autocomplete, MenuItem, Chip, useMediaQuery, Alert,
  FormControlLabel, Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import api from "../services/api"; // Axios instance
import { openAuthPdf } from "../services/openAuthPdf";
import PurchaseInvoiceImport from "./PurchaseInvoiceImport";
import { todayISO } from "../utils/date";

/* ================= MAIN COMPONENT ================= */
export default function PurchaseInvoiceForm({ openId, onConsumed }) {
  const [editId, setEditId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Opened from the "Needs Pricing" list — load that invoice straight into edit.
  useEffect(() => {
    if (openId) {
      setEditId(openId);
      onConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setImportOpen(true)}
        >
          Import from Excel
        </Button>
      </Box>

      <PurchaseInvoiceImport
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => setReloadKey((k) => k + 1)}
      />

      <InvoiceEditor editId={editId} setEditId={setEditId} onSaved={() => setReloadKey((k) => k + 1)} />
      <PurchaseList key={reloadKey} onEdit={setEditId} />
    </Box>
  );
}

/* Buy-vs-market hint shown under a line's Rate. Green when buying at or below
   today's market price, red when above. Renders nothing until the material is
   linked on the Market Price Link screen and has a current price. */
function MarketHint({ market, rate }) {
  if (!market || market.MarketPrice == null) return null;
  const mkt = Number(market.MarketPrice);
  if (!(mkt > 0)) return null;
  const r = Number(rate) || 0;
  let color = "text.secondary", txt = "";
  if (r > 0) {
    const pct = ((r - mkt) / mkt) * 100;
    const under = pct <= 0;
    color = under ? "success.main" : "error.main";
    txt = ` · ${Math.abs(pct).toFixed(0)}% ${under ? "under" : "over"}`;
  }
  return (
    <Typography variant="caption" sx={{ display: "block", color, lineHeight: 1.2, mt: 0.3 }}>
      Mkt ${mkt.toFixed(4)}{txt}
    </Typography>
  );
}

/* ================= INVOICE EDITOR ================= */
function InvoiceEditor({ editId, setEditId, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [master, setMaster] = useState({
    Master_ID: "", Customer_ID: "", Name: "", PO: "", Notes: "", Date: ""
  });
  const [details, setDetails] = useState([
    { Item_ID: "", Item_Description: "", G_Wt: 0, T_Wt: 0, N_Wt: 0, Rate: 0, L_Amount: 0 }
  ]);
  const [payment, setPayment] = useState({
    Method: "OnAccount",   // OnAccount | Cash | Check
    Amount: 0,             // editable for partial; defaults to invoice total
    AmountTouched: false,  // becomes true once the user types in Amount
    CheckNumber: "",
  });
  // Money already recorded against this invoice from a payment screen. The
  // form does not own those rows: editing the invoice leaves them exactly as
  // they are, and the panel below is locked so it cannot look otherwise.
  const [paidElsewhere, setPaidElsewhere] = useState({ count: 0, amount: 0 });
  const paymentLocked = paidElsewhere.count > 0;

  // Market prices per material (for the buy-vs-market hint on each rate cell).
  // Keyed by Item_ID. Empty until the Market Price Link screen maps materials.
  const [marketByItem, setMarketByItem] = useState({});

  // Fetch dropdown data
  useEffect(() => {
    api.get("/purchaseInvoices/accounts")
      .then(res => setAccounts(res.data))
      .catch(err => console.error("Accounts fetch failed:", err));

    api.get("/purchaseInvoices/products")
      .then(res => setProducts(res.data))
      .catch(err => console.error("Products fetch failed:", err));

    api.get("/prices/product-market")
      .then(res => {
        const map = {};
        (res.data?.items || []).forEach(x => { map[String(x.Item_ID)] = x; });
        setMarketByItem(map);
      })
      .catch(() => { /* market hint is optional — ignore if prices unavailable */ });
  }, []);

  // Load invoice for editing
  useEffect(() => {
    if (!editId) return;

    api.get(`/purchaseInvoices/${editId}`)
      .then(res => {
        const data = res.data;
        const m = data.master || { Master_ID: "", Customer_ID: "", Name: "", PO: "", Notes: "", Date: "" };
        // The date input needs YYYY-MM-DD; the API returns a full ISO datetime.
        setMaster({ ...m, Date: m.Date ? String(m.Date).split("T")[0] : "" });
        setDetails((data.details || []).map(d => ({
          ...d,
          G_Wt: Number(d.G_Wt),
          T_Wt: Number(d.T_Wt),
          N_Wt: Number(d.N_Wt),
          Rate: Number(d.Rate),
          L_Amount: Number(d.L_Amount)
        })));
        setPaidElsewhere(data.paymentsFromScreens || { count: 0, amount: 0 });
        // Hydrate the payment panel from the linked InvoicePayments row, if any
        if (data.payment) {
          setPayment({
            Method: data.payment.Method || "Cash",
            Amount: Number(data.payment.Amount) || 0,
            AmountTouched: true,
            CheckNumber: data.payment.CheckNumber || "",
          });
        } else {
          setPayment({ Method: "OnAccount", Amount: 0, AmountTouched: false, CheckNumber: "" });
        }
      })
      .catch(err => {
        console.error("Failed to load invoice:", err);
        alert("Invoice not found or server error");
        resetForm();
      });
  }, [editId]);

  // Reset form
  const resetForm = () => {
    setMaster({ Master_ID: "", Customer_ID: "", Name: "", PO: "", Notes: "", Date: "" });
    setDetails([{ Item_ID: "", Item_Description: "", G_Wt: 0, T_Wt: 0, N_Wt: 0, Rate: 0, L_Amount: 0 }]);
    setPayment({ Method: "OnAccount", Amount: 0, AmountTouched: false, CheckNumber: "" });
    setPaidElsewhere({ count: 0, amount: 0 });
  };

  useEffect(() => {
    if (!editId) resetForm();
  }, [editId]);

  // Row helpers
  const addRow = () => setDetails([...details, { Item_ID: "", Item_Description: "", G_Wt: 0, T_Wt: 0, N_Wt: 0, Rate: 0, L_Amount: 0 }]);
  const removeRow = i => details.length > 1 && setDetails(details.filter((_, idx) => idx !== i));

  // A "charge" product (e.g. Delivery) carries a flat dollar amount with no
  // weight × rate. Identified by its product group so new charge types can be
  // added in the catalog without code changes.
  const isChargeProduct = (itemId) =>
    products.find((p) => String(p.Item_ID) === String(itemId))?.Group_ID === "CHARGES";
  const isChargeRow = (row) => isChargeProduct(row.Item_ID);

  const updateRow = (i, field, value) => {
    const rows = [...details];
    if (field === "Item_Description") {
      rows[i][field] = value || "";
    } else if (field === "Item_ID") {
      rows[i].Item_ID = value || "";
      // Switching to a charge item: zero out weight/rate so the amount is the
      // only thing entered. Switching away resets the amount to weight × rate.
      if (isChargeProduct(value)) {
        rows[i].G_Wt = 0; rows[i].T_Wt = 0; rows[i].N_Wt = 0; rows[i].Rate = 0;
      } else {
        rows[i].L_Amount = +(rows[i].N_Wt * rows[i].Rate).toFixed(2);
      }
    } else if (field === "L_Amount") {
      // Direct amount entry, only used by charge rows.
      rows[i].L_Amount = value === "" ? 0 : parseFloat(value);
    } else {
      rows[i][field] = value === "" ? 0 : parseFloat(value);
      if (field === "G_Wt" || field === "T_Wt") {
        if (rows[i].T_Wt > rows[i].G_Wt) rows[i].T_Wt = rows[i].G_Wt;
        rows[i].N_Wt = +(rows[i].G_Wt - rows[i].T_Wt).toFixed(2);
      }
      rows[i].L_Amount = +(rows[i].N_Wt * rows[i].Rate).toFixed(2);
    }
    setDetails(rows);
  };

  // Save / update. `thenPrint` opens the finished invoice straight away, so
  // the common job — book the load, hand the supplier his copy — is one click
  // rather than save, find the row in the list, then View.
  const saveInvoice = async ({ thenPrint = false } = {}) => {
    try {
      const invoiceTotal = details.reduce((s, r) => s + (Number(r.L_Amount) || 0), 0);
      // Untouched means "pay it" — which here means whole dollars, the same
      // figure the Amount box has been showing.
      const payAmount = payment.AmountTouched
        ? Number(payment.Amount) || 0
        : Math.floor(invoiceTotal);
      const masterWithPayment = {
        ...master,
        Payment: {
          Method: payment.Method,
          Amount: payment.Method === "OnAccount" ? 0 : payAmount,
          CheckNumber: (payment.Method === "Check" || payment.Method === "ACH") ? (payment.CheckNumber || null) : null,
        },
      };
      let res;
      if (editId) res = await api.put(`/purchaseInvoices/${editId}`, { master: masterWithPayment, details });
      else res = await api.post("/purchaseInvoices", { master: masterWithPayment, details });

      const savedId = res.data.Master_ID || editId;
      const warn = (res.data.warnings || []).join("\n\n");
      if (warn) alert(res.data.message + "\n\n" + warn);
      else if (!thenPrint) alert(res.data.message);
      resetForm();
      setEditId(null);
      onSaved?.();
      // After the form is cleared, so a blocked popup never leaves a
      // half-reset screen behind.
      if (thenPrint && savedId) openAuthPdf(`/purchaseInvoices/${savedId}/pdf`);
    } catch (err) {
      console.error("Save failed:", err);
      // A 409 means the invoice is assigned to a broker — show that clear
      // instruction on its own, not buried behind "Failed to save invoice".
      const data = err.response?.data;
      const msg = (data && (data.error || data)) || err.message;
      if (err.response?.status === 409) alert(msg);
      else alert("Failed to save invoice: " + msg);
    }
  };

  // Totals
  const totalG = details.reduce((s, r) => s + r.G_Wt, 0).toFixed(2);
  const totalT = details.reduce((s, r) => s + r.T_Wt, 0).toFixed(2);
  const totalN = details.reduce((s, r) => s + r.N_Wt, 0).toFixed(2);
  const totalA = details.reduce((s, r) => s + r.L_Amount, 0).toFixed(2);
  const invoiceTotalNum = details.reduce((s, r) => s + (Number(r.L_Amount) || 0), 0);

  /* The yard does not count out pennies: a $1,900.45 load is paid $1,900 and
   * the 45c is forgiven. That already happens on the books — a purchase
   * invoice left short by under $5 is written off automatically — but the
   * Amount box still offered the total WITH the cents, so unless it was
   * retyped every single time, 45c went into the register that never left the
   * drawer. So the box now offers whole dollars and says what it did. Typing
   * over it still pays to the cent. */
  const roundedPayDefault = Math.floor(invoiceTotalNum);
  const centsForgiven = Number((invoiceTotalNum - roundedPayDefault).toFixed(2));
  const defaultPayAmount = invoiceTotalNum > 0 ? roundedPayDefault : 0;
  const effectivePayAmount = payment.AmountTouched ? Number(payment.Amount) || 0 : defaultPayAmount;

  return (
    <Box p={2}>
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" sx={{ color: "black" }}>{editId ? "Edit Purchase Invoice" : "Add Purchase Invoice"}</Typography>

        <Grid container spacing={2} mt={1}>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField type="date" label="Date" fullWidth InputLabelProps={{ shrink: true }}
              value={master.Date} onChange={e => setMaster({ ...master, Date: e.target.value })} />
          </Grid>
          <Grid size={{ xs: 12, sm: 8, md: 9 }}>
            <Autocomplete
              options={accounts}
              getOptionLabel={o => o.SubAccount_Name || ""}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={accounts.find(a => String(a.id) === String(master.Customer_ID)) || null}
              onChange={(e, v) => setMaster({ ...master, Customer_ID: v?.id || "", Name: v?.SubAccount_Name || "" })}
              renderInput={p => <TextField {...p} label="Customer" fullWidth />}
              fullWidth
              sx={{ width: "100%", minWidth: { xs: "100%", sm: 280 } }}
            />
          </Grid>
        </Grid>

        <TableContainer sx={{ overflowX: "auto", mt: 2 }}>
        <Table size="small" sx={{ minWidth: 720, "& .MuiTableCell-root": { color: "black" } }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Gross Weight</TableCell>
              <TableCell>Tare Weight</TableCell>
              <TableCell>Net Weight</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {details.map((r, i) => {
              const charge = isChargeRow(r);
              return (
              <TableRow key={i}>
                <TableCell>
                  <Autocomplete
                    options={products}
                    getOptionLabel={o => o.Product_Description || ""}
                    value={products.find(p => String(p.Item_ID) === String(r.Item_ID)) || null}
                    onChange={(e, v) => {
                      updateRow(i, "Item_ID", v?.Item_ID || "");
                      updateRow(i, "Item_Description", v?.Product_Description || "");
                    }}
                    renderInput={p => <TextField {...p} size="small" sx={{ minWidth: 180 }} />}
                  />
                </TableCell>
                {charge ? (
                  <>
                    <TableCell colSpan={3} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                      Flat charge (no weight)
                    </TableCell>
                    <TableCell />
                    <TableCell>
                      <TextField size="small" type="number" value={r.L_Amount}
                        onChange={e => updateRow(i, "L_Amount", e.target.value)}
                        inputProps={{ step: "0.01", min: "0" }} />
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell><TextField size="small" type="number" value={r.G_Wt} onChange={e => updateRow(i, "G_Wt", e.target.value)} /></TableCell>
                    <TableCell><TextField size="small" type="number" value={r.T_Wt} onChange={e => updateRow(i, "T_Wt", e.target.value)} /></TableCell>
                    <TableCell>{r.N_Wt}</TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={r.Rate} onChange={e => updateRow(i, "Rate", e.target.value)} />
                      <MarketHint market={marketByItem[String(r.Item_ID)]} rate={r.Rate} />
                    </TableCell>
                    <TableCell>{r.L_Amount}</TableCell>
                  </>
                )}
                <TableCell><Button color="error" onClick={() => removeRow(i)}>X</Button></TableCell>
              </TableRow>
              );
            })}
            <TableRow>
              <TableCell><b>Total</b></TableCell>
              <TableCell>{totalG}</TableCell>
              <TableCell>{totalT}</TableCell>
              <TableCell>{totalN}</TableCell>
              <TableCell />
              <TableCell>{totalA}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        </TableContainer>

        {/* ----- Payment panel ----- */}
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Payment</Typography>
          {paymentLocked && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>
                ${paidElsewhere.amount.toFixed(2)} is already recorded as paid on this invoice
              </strong>{" "}
              from the payment screens
              {paidElsewhere.count > 1 ? ` (${paidElsewhere.count} payments)` : ""}. Saving your
              changes here will <strong>not</strong> touch that money. To change what was paid,
              use the <strong>Pay</strong> button on Pending Invoices.
            </Alert>
          )}
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select fullWidth label="Method" disabled={paymentLocked}
                value={payment.Method}
                onChange={(e) => setPayment({ ...payment, Method: e.target.value })}
              >
                <MenuItem value="OnAccount">On Account (no cash entry)</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Check">Check</MenuItem>
                <MenuItem value="ACH">ACH (bank transfer)</MenuItem>
              </TextField>
            </Grid>
            {(payment.Method === "Check" || payment.Method === "ACH") && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth disabled={paymentLocked}
                  label={payment.Method === "ACH" ? "ACH Ref # (optional)" : "Check #"}
                  value={payment.CheckNumber}
                  onChange={(e) => setPayment({ ...payment, CheckNumber: e.target.value })}
                />
              </Grid>
            )}
            {payment.Method !== "OnAccount" && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth label="Amount" type="number" disabled={paymentLocked}
                  value={payment.AmountTouched ? payment.Amount : defaultPayAmount}
                  onChange={(e) => setPayment({
                    ...payment,
                    Amount: e.target.value === "" ? 0 : Number(e.target.value),
                    AmountTouched: true,
                  })}
                  helperText={
                    payment.AmountTouched
                      ? (invoiceTotalNum - effectivePayAmount > 5
                          ? "Partial payment"
                          : effectivePayAmount < invoiceTotalNum
                          ? `$${(invoiceTotalNum - effectivePayAmount).toFixed(2)} written off`
                          : "")
                      : centsForgiven > 0
                      ? `Whole dollars — the ${Math.round(centsForgiven * 100)}¢ is written off`
                      : "Defaults to invoice total"}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
            )}
            {payment.Method !== "OnAccount" && (
              <Grid size={{ xs: 12 }}>
                <Chip
                  size="small"
                  color={effectivePayAmount > 0 ? "success" : "default"}
                  variant="outlined"
                  label={
                    effectivePayAmount > 0
                      ? `Will post ${payment.Method} payment of $${effectivePayAmount.toFixed(2)} to Daily Cash on ${master.Date || "today"}`
                      : "No payment will be posted (amount is 0)"
                  }
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        <Box mt={1}>
          <Button onClick={addRow}>+ Add Item</Button>
          <Button variant="contained" sx={{ ml: 2 }} onClick={() => saveInvoice()}>
            {editId ? "Update" : "Save"}
          </Button>
          <Button variant="outlined" sx={{ ml: 1 }} onClick={() => saveInvoice({ thenPrint: true })}>
            {editId ? "Update & Print" : "Save & Print"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

/* ================= PURCHASE LIST ================= */
function PurchaseList({ onEdit }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const today = todayISO();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  /* The dates start at today, so typing a customer's name found nothing
   * unless the range was widened first - which made the Customer box look
   * broken. With this on, the dates are ignored and you get everything that
   * customer has ever had. */
  const [allDates, setAllDates] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All"); // All | Open | Partial | Paid

  const fetchRows = useCallback(() => {
    api.get("/purchaseInvoices", { params: allDates ? { name: search } : { name: search, fromDate, toDate } })
      .then(res => setRows(res.data))
      .catch(err => console.error("Failed to load purchase list:", err));
  }, [search, fromDate, toDate, allDates]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filtered = useMemo(
    () => statusFilter === "All"
      ? rows
      : rows.filter((r) => (r.PaymentStatus || "Open") === statusFilter),
    [rows, statusFilter]
  );

  const totals = useMemo(() => {
    let t = 0, p = 0, b = 0;
    for (const r of filtered) {
      t += Number(r.TotalAmount) || 0;
      p += Number(r.PaidAmount)  || 0;
      b += Number(r.Balance)     || 0;
    }
    return { total: t, paid: p, balance: b };
  }, [filtered]);

  const del = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/purchaseInvoices/${id}`);
      setRows(prev => prev.filter(x => x.Master_ID !== id));
      alert("Deleted successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to delete: " + (err.response?.data || err.message));
    }
  };

  const statusChip = (s) => {
    const status = s || "Open";
    const color = status === "Paid" ? "success" : status === "Partial" ? "warning" : "error";
    return <Chip size="small" label={status} color={color} />;
  };

  return (
    <Box p={1}>
      <Typography variant="h6" sx={{ color: "black" }}>Purchase Invoices</Typography>

      <Paper sx={{ p: 2, mb: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField label="Customer" fullWidth value={search} onChange={e => setSearch(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth disabled={allDates} value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth disabled={allDates} value={toDate} onChange={e => setToDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={allDates} onChange={e => setAllDates(e.target.checked)} />}
              label="All dates"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select fullWidth label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="Partial">Partial</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
          <Chip variant="outlined" label={`${filtered.length} invoice${filtered.length === 1 ? "" : "s"}`} />
          <Chip variant="outlined" color="primary" label={`Total: $${totals.total.toFixed(2)}`} />
          <Chip variant="outlined" color="success" label={`Paid: $${totals.paid.toFixed(2)}`} />
          <Chip variant="outlined" color="error"   label={`Open Balance: $${totals.balance.toFixed(2)}`} />
        </Box>
      </Paper>

      {(() => {
        const rowActions = (r) => (
          <>
            <Button size="small" onClick={() => onEdit(r.Master_ID)}>Edit</Button>
            <Button size="small" color="error" onClick={() => del(r.Master_ID)}>Delete</Button>
            <Button size="small" color="success" onClick={() => openAuthPdf(`/purchaseInvoices/${r.Master_ID}/pdf`)}>View</Button>
            <Button size="small" color="warning" onClick={() => openAuthPdf(`/purchaseInvoices/${r.Master_ID}/bol`)}>BOL</Button>
          </>
        );
        return isMobile ? (
          /* ---- Mobile: stacked cards ---- */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {filtered.length === 0 ? (
              <Typography align="center" sx={{ color: "text.secondary", py: 3 }}>No invoices found.</Typography>
            ) : filtered.map((r) => (
              <Paper key={r.Master_ID} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 700 }}>#{r.Master_ID}</Typography>
                  {statusChip(r.PaymentStatus)}
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>{r.Name} · {r.Date ? String(r.Date).split("T")[0] : "-"}</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "text.secondary" }}>
                  <span>Amount {(Number(r.TotalAmount) || 0).toFixed(2)}</span>
                  <span>Paid {(Number(r.PaidAmount) || 0).toFixed(2)}</span>
                  <span style={{ fontWeight: 700, color: (Number(r.Balance) || 0) > 0.01 ? "#d32f2f" : "inherit" }}>
                    Bal {(Number(r.Balance) || 0).toFixed(2)}
                  </span>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>{rowActions(r)}</Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(r => (
                  <TableRow key={r.Master_ID}>
                    <TableCell>{r.Master_ID}</TableCell>
                    <TableCell>{r.Name}</TableCell>
                    <TableCell>{r.Date ? String(r.Date).split("T")[0] : "-"}</TableCell>
                    <TableCell align="right">{(Number(r.TotalAmount) || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{(Number(r.PaidAmount)  || 0).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: (Number(r.Balance) || 0) > 0.01 ? "error.main" : "inherit" }}>
                      {(Number(r.Balance) || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>{statusChip(r.PaymentStatus)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{rowActions(r)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      })()}
    </Box>
  );
}
